// @ts-nocheck
/*
 * Port of views/dashboard/did-history/list.js (390 lines).
 * BYTE-IDENTICAL — dual tab (Allocated / Removed), date-range + DID filter
 * with per-tab unique DID extraction, exportToCSV includes toUser/removeFrom
 * bifurcation. Chrome swap: PrimeReact TabView → react-bootstrap Tabs;
 * PrimeReact DataTable → obd3-gui.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '@/components/partials/components/Loader';
import type { ColumnDef } from '@tanstack/react-table';
import { API_ALLOCATED_DID, API_REMOVE_DID } from '@/service/url';
import { getApiCall } from '@/service/httpCall';
import { errorCheck } from '@/views/dashboard/auth/auth-utils';
import { exportToCSV } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Download, Phone, PlusCircle, MinusCircle } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';

const dateToInputStr = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const inputStrToLocalDate = (str) => {
  if (!str) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
  if (!m) {
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const DIDHistory = () => {
  const navigate = useNavigate();

  const [didAllocateList, setDidAllocateList] = useState([]);
  const [didRemoveList, setDidRemoveList] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [didList, setDidList] = useState([]);
  const [selectedDid, setSelectedDid] = useState(null);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  const keepFiltersRef = useRef(false);
  const rangedRef = useRef(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([getApiCall(API_ALLOCATED_DID, {}), getApiCall(API_REMOVE_DID, {})])
      .then(([allocatedRes, removedRes]) => {
        setDidAllocateList(allocatedRes);
        setDidRemoveList(removedRes);
        setFilteredData(allocatedRes);
        const allocatedDids = extractUniqueDids(allocatedRes);
        setDidList(allocatedDids);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  const extractUniqueDids = (data) =>
    Array.from(
      new Map(
        data
          .filter((item) => item.did)
          .map((item) => [item.did.id, { id: item.did.id, actual_number: item.did.actual_number }]),
      ).values(),
    );

  const applyFilters = (list) => {
    const start = startDate ? new Date(startDate).setHours(0, 0, 0, 0) : null;
    const end = endDate ? new Date(endDate).setHours(23, 59, 59, 999) : null;
    return list.filter((item) => {
      const matchDate =
        !start || !end
          ? true
          : (() => {
              const itemDate = new Date(item.created_at).getTime();
              return itemDate >= start && itemDate <= end;
            })();
      const matchDid = !selectedDid || String(item.did?.id) === String(selectedDid);
      return matchDate && matchDid;
    });
  };

  useEffect(() => {
    const currentList = activeIndex === 0 ? didAllocateList : didRemoveList;
    const currentDidList = extractUniqueDids(currentList);
    if (keepFiltersRef.current) {
      keepFiltersRef.current = false;
      setDidList((prev) => {
        const kept = prev.find((d) => String(d.id) === String(selectedDid));
        return kept && !currentDidList.some((d) => String(d.id) === String(selectedDid))
          ? [...currentDidList, kept]
          : currentDidList;
      });
      setFilteredData(applyFilters(currentList));
      return;
    }
    setFilteredData(currentList);
    setDidList(currentDidList);
    setSelectedDid(null);
  }, [activeIndex, didAllocateList, didRemoveList]);

  const loadLists = (params, keepFilters) => {
    setLoading(true);
    Promise.all([getApiCall(API_ALLOCATED_DID, params), getApiCall(API_REMOVE_DID, params)])
      .then(([allocatedRes, removedRes]) => {
        keepFiltersRef.current = keepFilters;
        rangedRef.current = keepFilters;
        setDidAllocateList(allocatedRes);
        setDidRemoveList(removedRes);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (toastProps) setShowToast(true);
  }, [toastProps]);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleFilter = () => {
    if (startDate && endDate) {
      loadLists({ start_date: dateToInputStr(startDate), end_date: dateToInputStr(endDate) }, true);
      return;
    }
    setFilteredData(applyFilters(activeIndex === 0 ? didAllocateList : didRemoveList));
  };

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedDid(null);
    if (rangedRef.current) {
      loadLists({}, false);
      return;
    }
    setFilteredData(activeIndex === 0 ? didAllocateList : didRemoveList);
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'did-history',
        data: filteredData,
        columns: [
          { header: '#', accessor: (_, index) => index + 1 },
          { header: 'DID', accessor: (row) => row.did?.actual_number || '-' },
          { header: 'Allocated / Removed User', accessor: (row) => row.toUser?.name || row.removeFrom?.name || '-' },
          { header: 'Allocated / Removed By', accessor: (row) => row.fromUser?.username || row.removeBy?.username || '-' },
          { header: 'Voice Plan', accessor: (row) => row.voicePlan?.plan_name || '-' },
          { header: 'Action', accessor: (row) => row.action || '-' },
          {
            header: 'Created Date',
            accessor: (row) => new Date(row.created_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
            }),
          },
          {
            header: 'Updated Date',
            accessor: (row) => new Date(row.updated_at).toLocaleString('en-IN', {
              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
            }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const onError = (err) => {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.error(err);
    }
  };

  if (loading && didAllocateList.length === 0 && didRemoveList.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const dateFormatter = (v: any) =>
    v
      ? new Date(v).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '—';

  const didCell = ({ row }: any) => (
    <div className="flex items-center gap-2.5">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
        <Phone className="h-4 w-4" />
      </span>
      <span className="font-mono text-sm font-medium text-foreground tabular-nums">
        {row.original.did?.actual_number || '—'}
      </span>
    </div>
  );

  const dateCell = (getter: (r: any) => any) => ({ row }: any) => (
    <span className="whitespace-nowrap text-xs text-muted-foreground tabular-nums">
      {dateFormatter(getter(row.original))}
    </span>
  );

  const plainCell = (getter: (r: any) => any) => ({ row }: any) => (
    <span className="text-sm text-foreground">{getter(row.original) || '—'}</span>
  );

  const actionCell = ({ row }: any) => {
    const action = row.original.action;
    if (!action) return <span className="text-muted-foreground">—</span>;
    const isAdd = /alloc|assign|add/i.test(action);
    return (
      <span
        className={
          'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
          (isAdd
            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
        }
      >
        {isAdd ? <PlusCircle className="h-3 w-3" /> : <MinusCircle className="h-3 w-3" />}
        <span className="capitalize">{action}</span>
      </span>
    );
  };

  const allocatedColumns: ColumnDef<any>[] = [
    { id: '#', header: '#', size: 60, cell: ({ row }) => (
      <span className="text-xs text-muted-foreground tabular-nums">{row.index + 1}</span>
    ) },
    { accessorFn: (row: any) => row.did?.actual_number, id: 'did', header: 'DID', size: 220, cell: didCell },
    { accessorFn: (row: any) => row.toUser?.name, id: 'toUser', header: 'Allocated To', cell: plainCell((r: any) => r.toUser?.name) },
    { accessorFn: (row: any) => row.fromUser?.username, id: 'fromUser', header: 'Allocated By', cell: plainCell((r: any) => r.fromUser?.username) },
    { accessorFn: (row: any) => row.voicePlan?.plan_name, id: 'voicePlan', header: 'Voice Plan', cell: plainCell((r: any) => r.voicePlan?.plan_name) },
    { accessorKey: 'action', header: 'Action', size: 140, cell: actionCell },
    { accessorKey: 'created_at', header: 'Created', cell: dateCell((r: any) => r.created_at) },
    { accessorKey: 'updated_at', header: 'Updated', cell: dateCell((r: any) => r.updated_at) },
  ];

  const removedColumns: ColumnDef<any>[] = [
    { id: '#', header: '#', size: 60, cell: ({ row }) => (
      <span className="text-xs text-muted-foreground tabular-nums">{row.index + 1}</span>
    ) },
    { accessorFn: (row: any) => row.did?.actual_number, id: 'did', header: 'DID', size: 220, cell: didCell },
    { accessorFn: (row: any) => row.removeFrom?.name, id: 'removeFrom', header: 'Removed From', cell: plainCell((r: any) => r.removeFrom?.name) },
    { accessorFn: (row: any) => row.removeBy?.username, id: 'removeBy', header: 'Removed By', cell: plainCell((r: any) => r.removeBy?.username) },
    { accessorFn: (row: any) => row.voicePlan?.plan_name, id: 'voicePlan', header: 'Voice Plan', cell: plainCell((r: any) => r.voicePlan?.plan_name) },
    { accessorKey: 'action', header: 'Action', size: 140, cell: actionCell },
    { accessorKey: 'created_at', header: 'Created', cell: dateCell((r: any) => r.created_at) },
    { accessorKey: 'updated_at', header: 'Updated', cell: dateCell((r: any) => r.updated_at) },
  ];

  return (
    <FillPage>
      <PageHeader
        title="DID History"
        description="Provision / re-assignment history for DID numbers, filterable by date."
        actions={
          filteredData.length > 0 && (
            <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
              <Download className="h-4 w-4" />
              Export
            </Button>
          )
        }
      />
      {/* Segmented tabs — count badges baked into each tab so the
          stat strip and tab row share one line. */}
      <div className="mb-3">
        <div
          className="inline-flex h-10 items-center gap-1 rounded-full border border-border/60 bg-card p-1"
          style={{
            boxShadow:
              'inset 2px 2px 5px rgba(163,177,198,0.35), inset -2px -2px 5px rgba(255,255,255,0.9)',
          }}
        >
          {[
            {
              key: 0,
              label: 'Allocated',
              count: didAllocateList.length,
              icon: PlusCircle,
            },
            {
              key: 1,
              label: 'Removed',
              count: didRemoveList.length,
              icon: MinusCircle,
            },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeIndex === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveIndex(tab.key)}
                className={
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30 ' +
                  (active
                    ? 'bg-primary text-primary-foreground shadow-[0_2px_5px_rgba(0,0,0,0.15)]'
                    : 'text-muted-foreground hover:bg-primary/10 hover:text-primary')
                }
              >
                <Icon className="h-3.5 w-3.5" />
                {tab.label}
                <span
                  className={
                    'ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ' +
                    (active ? 'bg-white/20 text-white' : 'bg-primary/15 text-primary')
                  }
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Filter toolbar */}
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            From
          </label>
          <CalendarDateInput
            value={dateToInputStr(startDate)}
            onChange={(e) => setStartDate(inputStrToLocalDate(e))}
            className="form-control"
            style={{ height: '40px', width: '170px' }}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            To
          </label>
          <CalendarDateInput
            value={dateToInputStr(endDate)}
            onChange={(e) => setEndDate(inputStrToLocalDate(e))}
            className="form-control"
            style={{ height: '40px', width: '170px' }}
          />
        </div>
        <div>
          <label className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            DID
          </label>
          <select
            className="form-control form-select"
            value={selectedDid || ''}
            onChange={(e) => setSelectedDid(e.target.value || null)}
            style={{ height: '40px', width: '200px' }}
          >
            <option value="">All DIDs</option>
            {didList.map((did) => (
              <option key={did.id} value={did.id}>
                {did.actual_number}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleFilter}>Filter</Button>
          <Button variant="outline" onClick={handleReset}>
            Reset
          </Button>
        </div>
      </div>

      <DataTable
        tableId={
          activeIndex === 0
            ? 'did-history.allocated:v2'
            : 'did-history.removed:v2'
        }
        columns={activeIndex === 0 ? allocatedColumns : removedColumns}
        data={filteredData}
        searchPlaceholder="Search DID history…"
        emptyTitle="No DID history"
        emptyDescription="No records match the current filters."
        pageSize={10}
        disableExport
      />
    </FillPage>
  );
};

export default DIDHistory;
